import Calendar16 from '@carbon/icons-react/es/calendar/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Person16 from '@carbon/icons-react/es/person/16';
import Task16 from '@carbon/icons-react/es/task/16';
import {
  ICellRendererParams,
  IHeaderParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { Dictionary } from 'lodash';
import React, { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { DateType } from 'api/models';
import {
  ProjectFinancialsGroupBy,
  ProjectFinancialsSourceType,
  ProjectFinancialsTableRowData,
} from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import { ReactComponent as PhaseIcon } from 'assets/svgs/phase-timesheet-settings.svg';
import { Avatar } from 'components/atoms/avatar';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { formatterWithMinutes } from 'components/atoms/input-duration/formatter';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { ValueFormatterParams } from 'components/organisms/templates/projects/plan/list-view/types';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { percentageRenderer } from '../utils/common';
import {
  HeaderCellWrapper,
  PlaceholderDataWrapper,
  ProjectFinancialsTableNameCellRenderer,
  Result16,
  ResourceNameCellWrapper,
} from './common';

const allocationUnsupportedTypes = {
  [ProjectFinancialsGroupBy.Role]: {
    [ProjectFinancialsSourceType.Phase]: 2,
    [ProjectFinancialsSourceType.Adhoc]: 2,
    [ProjectFinancialsSourceType.Google]: 2,
    [ProjectFinancialsSourceType.Outlook]: 2,
    [ProjectFinancialsSourceType.Project]: 2,
    [ProjectFinancialsSourceType.Team]: 1,
  },
  [ProjectFinancialsGroupBy.Phase]: {
    [ProjectFinancialsSourceType.Activity]: 0,
    [ProjectFinancialsSourceType.Google]: 0,
    [ProjectFinancialsSourceType.Outlook]: 0,
    [ProjectFinancialsSourceType.Meeting]: 0,
    [ProjectFinancialsSourceType.Phase]: 1,
    [ProjectFinancialsSourceType.Adhoc]: 1,
    [ProjectFinancialsSourceType.Project]: 0,
    [ProjectFinancialsSourceType.User]: {
      [ProjectFinancialsSourceType.Activity]: 0,
      [ProjectFinancialsSourceType.Google]: 0,
      [ProjectFinancialsSourceType.Outlook]: 0,
      [ProjectFinancialsSourceType.Meeting]: 0,
      [ProjectFinancialsSourceType.Phase]: 1,
      [ProjectFinancialsSourceType.Adhoc]: 1,
      [ProjectFinancialsSourceType.Project]: 0,
    },
    [ProjectFinancialsSourceType.Team]: {
      [ProjectFinancialsSourceType.Activity]: 0,
      [ProjectFinancialsSourceType.Google]: 0,
      [ProjectFinancialsSourceType.Outlook]: 0,
      [ProjectFinancialsSourceType.Meeting]: 0,
      [ProjectFinancialsSourceType.Phase]: 1,
      [ProjectFinancialsSourceType.Adhoc]: 1,
      [ProjectFinancialsSourceType.Project]: 0,
    },
  },
};

const DASH = '-';

const valueOrDashGetter = (
  params: ValueGetterParams,
  value: number,
  groupBy: ProjectFinancialsGroupBy,
  isEstimateColumn = false,
) => {
  const {
    data,
    node: { parent },
  } = params;
  const rowData = data as ProjectFinancialsTableRowData;
  const parentRowData =
    parent != null && parent.data != null
      ? (parent.data as ProjectFinancialsTableRowData)
      : undefined;

  const { sourceType, hierarchy } = rowData;
  const hierarchyThreshold = allocationUnsupportedTypes[groupBy][sourceType];

  let shouldShowDash = false;

  if (
    (sourceType === ProjectFinancialsSourceType.Team
      ? !isEstimateColumn
      : isEstimateColumn) &&
    hierarchyThreshold != null
  ) {
    if (typeof hierarchyThreshold === 'number') {
      shouldShowDash = hierarchy.length > hierarchyThreshold;
    } else if (parentRowData != null) {
      const { sourceType: parentSourceType } = parentRowData;

      shouldShowDash =
        hierarchyThreshold[parentSourceType] != null &&
        hierarchy.length > hierarchyThreshold[parentSourceType];
    }
  }

  return shouldShowDash ? DASH : value;
};

const valueOrDashFormatter = (
  { value }: ValueFormatterParams,
  formatterFn: (value: any, ...props) => string,
  ...formatterProps
) => (value === '-' ? '-' : formatterFn(value, ...formatterProps));

export const revenueGetter = (
  params: ValueGetterParams,
  date: DateType,
  groupBy: ProjectFinancialsGroupBy,
  isEstimateColumn = false,
): ReactNode =>
  valueOrDashGetter(
    params,
    isEstimateColumn
      ? params.data.breakdownDetails[date]?.estimatedBreakdown.revenue
      : params.data.breakdownDetails[date]?.actualBreakdown.revenue,
    groupBy,
    isEstimateColumn,
  );

export const costGetter = (
  params: ValueGetterParams,
  date: DateType,
  groupBy: ProjectFinancialsGroupBy,
  isEstimateColumn = false,
): ReactNode =>
  valueOrDashGetter(
    params,
    isEstimateColumn
      ? params.data.breakdownDetails[date]?.estimatedBreakdown.cost
      : params.data.breakdownDetails[date]?.actualBreakdown.cost,
    groupBy,
    isEstimateColumn,
  );

export const profitGetter = (
  params: ValueGetterParams,
  date: DateType,
  groupBy: ProjectFinancialsGroupBy,
  isEstimateColumn = false,
): ReactNode =>
  valueOrDashGetter(
    params,
    isEstimateColumn
      ? params.data.breakdownDetails[date]?.estimatedBreakdown.profit
      : params.data.breakdownDetails[date]?.actualBreakdown.profit,
    groupBy,
    isEstimateColumn,
  );

export const marginGetter = (
  params: ValueGetterParams,
  date: DateType,
  groupBy: ProjectFinancialsGroupBy,
  isEstimateColumn = false,
): ReactNode =>
  valueOrDashGetter(
    params,
    isEstimateColumn
      ? params.data.breakdownDetails[date]?.estimatedBreakdown.margin
      : params.data.breakdownDetails[date]?.actualBreakdown.margin,
    groupBy,
    isEstimateColumn,
  );

export const timeTrackedGetter = (
  params: ValueGetterParams,
  date: DateType,
): ReactNode => {
  return params.data.breakdownDetails[date]?.timeTracked;
};

export const timeAllocatedGetter = (
  params: ValueGetterParams,
  date: DateType,
  groupBy: ProjectFinancialsGroupBy,
): ReactNode =>
  valueOrDashGetter(
    params,
    params.data.breakdownDetails[date]?.timeAllocated,
    groupBy,
    true,
  );

export const budgetFormatter = (
  params: ValueFormatterParams,
  currencyCode: string,
): string => valueOrDashFormatter(params, currencyRenderer, currencyCode);

export const hoursFormatter = (params: ValueFormatterParams): string =>
  valueOrDashFormatter(params, formatterWithMinutes);

export const percentageFormatter = (params: ValueFormatterParams): string =>
  valueOrDashFormatter(params, percentageRenderer);

export const ColumnHeaderRenderer: FC<IHeaderParams> = (params) => {
  const { displayName } = params;

  return <HeaderCellWrapper>{displayName}</HeaderCellWrapper>;
};

export const GroupHeaderRenderer: FC<
  IHeaderParams & { groupBy: ProjectFinancialsGroupBy }
> = (params) => {
  const { groupBy } = params;

  return (
    <HeaderCellWrapper>
      <FormattedMessage id={`project.financials.table.${groupBy}`} />
    </HeaderCellWrapper>
  );
};

export const GroupCellRenderer: FC<
  ICellRendererParams & {
    groupBy: ProjectFinancialsGroupBy;
    taskStatusOptionsLookup: Dictionary<StatusFieldOption>;
  }
> = (params) => {
  const { data, groupBy, taskStatusOptionsLookup } = params;
  const rowData = data as ProjectFinancialsTableRowData;
  const { sourceType, sourceName, isAlias, childCount } = rowData;

  let rendererPrefix: ReactNode;
  let rendererSuffix: ReactNode;
  let rendererContent: ReactNode = rowData.sourceName;

  switch (sourceType) {
    case ProjectFinancialsSourceType.Role:
      rendererPrefix = <Avatar name={sourceName} />;
      rendererContent = <strong>{sourceName}</strong>;
      rendererSuffix = (
        <>
          {childCount}
          <Person16 />
        </>
      );
      break;
    case ProjectFinancialsSourceType.User:
      rendererPrefix = (
        <Avatar
          name={sourceName}
          src={getThumbnailUrl(rowData.user?.photoUrl)}
        />
      );
      rendererContent = (
        <ResourceNameCellWrapper>
          <>{sourceName}</>
          {rowData.referenceTeam != null ? (
            <PlaceholderDataWrapper>
              <PlaceholderAvatar isCustomerRole={false} size={12} />
              <>{rowData.referenceTeam.teamName}</>
            </PlaceholderDataWrapper>
          ) : undefined}
        </ResourceNameCellWrapper>
      );
      break;
    case ProjectFinancialsSourceType.Team:
      rendererPrefix = (
        <PlaceholderAvatar isCustomerRole={false} size="default" />
      );
      rendererContent = (
        <ResourceNameCellWrapper>
          <>{sourceName}</>
        </ResourceNameCellWrapper>
      );
      break;
    case ProjectFinancialsSourceType.Project:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Phase ? <Task16 /> : undefined;
      rendererContent =
        groupBy === ProjectFinancialsGroupBy.Role ? (
          <FormattedMessage
            id="project.financials.table.cell.reported_on"
            values={{
              resource: <strong>&nbsp;{sourceType.toLowerCase()}</strong>,
            }}
          />
        ) : (
          <strong>
            <FormattedMessage
              id="project.financials.table.cell.reported_on"
              values={{
                resource: <>&nbsp;{sourceType.toLowerCase()}</>,
              }}
            />
          </strong>
        );

      break;
    case ProjectFinancialsSourceType.Phase:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Phase && !isAlias ? (
          <PhaseIcon />
        ) : undefined;

      rendererContent =
        groupBy === ProjectFinancialsGroupBy.Role || isAlias ? (
          <FormattedMessage
            id="project.financials.table.cell.reported_on"
            values={{
              resource: (
                <strong>
                  &nbsp;
                  {groupBy === ProjectFinancialsGroupBy.Role
                    ? sourceName
                    : sourceType.toLowerCase()}
                </strong>
              ),
            }}
          />
        ) : (
          <strong>{sourceName}</strong>
        );
      break;
    case ProjectFinancialsSourceType.Task:
      rendererPrefix =
        rowData.task != null &&
        rowData.task.fields != null &&
        rowData.task.fields.length > 0 ? (
          <StatusIndicator
            status={
              taskStatusOptionsLookup[
                rowData.task.fields[0].fieldValue as number
              ]
            }
            type={rowData.task.type}
          />
        ) : undefined;
      break;
    case ProjectFinancialsSourceType.Activity:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Phase ? <Result16 /> : undefined;
      rendererContent =
        groupBy === ProjectFinancialsGroupBy.Phase ? (
          <strong>
            <FormattedMessage
              id={`project.financials.table.cell.${sourceType}`}
            />
          </strong>
        ) : undefined;
      break;
    case ProjectFinancialsSourceType.Adhoc:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Role ? <Result16 /> : undefined;
      rendererContent = sourceName;
      break;
    case ProjectFinancialsSourceType.Google:
    case ProjectFinancialsSourceType.Outlook:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Role ? <Calendar16 /> : undefined;
      rendererContent = sourceName;

      break;
    case ProjectFinancialsSourceType.Meeting:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Phase ? <Calendar16 /> : undefined;
      rendererContent =
        groupBy === ProjectFinancialsGroupBy.Phase ? (
          <strong>
            <FormattedMessage
              id={`project.financials.table.cell.${sourceType}`}
            />
          </strong>
        ) : undefined;
      break;
    case ProjectFinancialsSourceType.NonPhaseTask:
      rendererPrefix =
        groupBy === ProjectFinancialsGroupBy.Phase ? (
          <ListChecked16 />
        ) : undefined;
      rendererContent =
        groupBy === ProjectFinancialsGroupBy.Phase ? (
          <strong>
            <FormattedMessage
              id={`project.financials.table.cell.${sourceType}`}
            />
          </strong>
        ) : undefined;
      break;
    default:
      break;
  }

  return (
    <ProjectFinancialsTableNameCellRenderer
      prefix={rendererPrefix}
      suffix={rendererSuffix}
    >
      {rendererContent}
    </ProjectFinancialsTableNameCellRenderer>
  );
};
